/**
 * @fileoverview Exceptions Chart
 * @description Exception counts grouped by exception type over time.
 */

import React, { useMemo } from 'react';
import { Typography, Paper, Skeleton, Box } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';

const EXCEPTION_COLORS = ['#d32f2f', '#f44336', '#e57373', '#ef9a9a', '#ffcdd2', '#b71c1c'];

const ExceptionsChart = ({ data, loading }) => {
  const rows = data?.rows || [];

  const { timestamps, series } = useMemo(() => {
    if (!rows.length) return { timestamps: [], series: [] };

    // Group by timestamp, aggregate by type
    const byTime = {};
    const typeSet = new Set();
    rows.forEach((r) => {
      const ts = r.timestamp;
      if (!byTime[ts]) byTime[ts] = {};
      const type = r.type || 'Unknown';
      typeSet.add(type);
      byTime[ts][type] = (byTime[ts][type] || 0) + (r.count || 0);
    });

    const sortedTimestamps = Object.keys(byTime).sort((a, b) => a.localeCompare(b));
    const types = [...typeSet];

    const chartSeries = types.map((type, i) => ({
      data: sortedTimestamps.map((ts) => byTime[ts][type] || 0),
      label: type.length > 30 ? type.substring(0, 30) + '…' : type,
      color: EXCEPTION_COLORS[i % EXCEPTION_COLORS.length],
      stack: 'exceptions',
    }));

    return {
      timestamps: sortedTimestamps.map((ts) => {
        const d = new Date(ts);
        return isNaN(d.getTime()) ? ts : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }),
      series: chartSeries,
    };
  }, [rows]);

  if (loading) {
    return <Skeleton variant="rounded" height={300} sx={{ mb: 3 }} />;
  }

  if (!timestamps.length) {
    return (
      <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">No exception data available.</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#d32f2f' }}>
        Exceptions by Type
      </Typography>
      <Box sx={{ width: '100%', height: 300 }}>
        <BarChart
          height={270}
          xAxis={[{ data: timestamps, scaleType: 'band' }]}
          series={series}
          slotProps={{ legend: { direction: 'row', position: { vertical: 'top', horizontal: 'middle' } } }}
        />
      </Box>
    </Paper>
  );
};

export default ExceptionsChart;
